<template>
  <div class="consult-workbench">
    <div class="page-head">
      <div>
        <h2>咨询管理</h2>
        <p>左侧选择小程序用户咨询，右侧像聊天一样回复用户消息。</p>
      </div>
      <el-button size="small" icon="el-icon-refresh" @click="refreshCurrent">刷新</el-button>
    </div>

    <div class="workbench-card">
      <aside class="session-pane">
        <div class="session-filter">
          <el-input
            v-model="filters.keyword"
            size="small"
            prefix-icon="el-icon-search"
            placeholder="客户编号 / 咨询编号 / 用户 / 内容"
            clearable
            @keyup.enter.native="search"
            @clear="search"
          />
          <el-select v-model="filters.status" size="small" placeholder="全部状态" clearable @change="search">
            <el-option label="待处理" value="pending" />
            <el-option label="跟进中" value="following" />
            <el-option label="已关闭" value="closed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
        </div>

        <div v-loading="loading" class="session-list">
          <button
            v-for="item in rows"
            :key="item.id"
            type="button"
            class="session-item"
            :class="{ active: selected && selected.id === item.id }"
            @click="selectSession(item)"
          >
            <div class="session-avatar">{{ avatarText(item) }}</div>
            <div class="session-main">
              <div class="session-top">
                <span class="session-name">{{ item.nickname || item.name || '小程序用户' }}</span>
                <span class="session-time">{{ formatTime(item.updated_at || item.created_at) }}</span>
              </div>
              <div class="session-source">{{ item.source_page || item.source_type || '小程序在线咨询' }}</div>
              <div class="session-code">{{ item.customer_no || '-' }} · {{ item.consultation_no || '-' }}</div>
              <div class="session-preview">{{ item.last_message || item.content || '暂无消息内容' }}</div>
            </div>
            <span class="session-status" :class="`status-${item.status || 'pending'}`">{{ statusText(item.status) }}</span>
          </button>

          <div v-if="!loading && !rows.length" class="session-empty">暂无咨询会话</div>
        </div>

        <div class="session-pager">
          <el-pagination
            small
            layout="prev, pager, next"
            :current-page="page"
            :page-size="pageSize"
            :total="total"
            @current-change="page = $event; fetchList(false)"
          />
        </div>
      </aside>

      <main class="chat-pane">
        <template v-if="selected">
          <header class="chat-header">
            <div>
              <div class="chat-title">{{ selected.nickname || selected.name || '小程序用户' }}</div>
              <div class="chat-subtitle">
                <span>{{ selectedDetail.source_page || selected.source_page || '小程序在线咨询' }}</span>
                <span v-if="selectedDetail.phone">手机号：{{ selectedDetail.phone }}</span>
                <span>客户编号：{{ selectedDetail.customer_no || selected.customer_no || '-' }}</span>
                <span>咨询编号：{{ selectedDetail.consultation_no || selected.consultation_no || '-' }}</span>
              </div>
            </div>
            <div class="chat-actions">
              <el-select v-model="selected.status" size="small" class="status-select" @change="saveStatus">
                <el-option label="待处理" value="pending" />
                <el-option label="跟进中" value="following" />
                <el-option label="已关闭" value="closed" />
                <el-option label="已取消" value="cancelled" />
              </el-select>
              <el-button size="small" @click="fetchMessages(selected.id)">刷新消息</el-button>
            </div>
          </header>

          <section ref="messageList" v-loading="messageLoading" class="message-list">
            <div v-if="!messageLoading && !messages.length" class="message-empty">暂无聊天消息</div>
            <div
              v-for="message in messages"
              :key="message.id"
              class="message-row"
              :class="{ mine: message.sender_type === 'admin' }"
            >
              <div class="message-avatar">{{ message.sender_type === 'admin' ? '管' : avatarText(selected) }}</div>
              <div class="message-body">
                <div class="message-name">
                  {{ message.sender_type === 'admin' ? (message.admin_name || '后台客服') : (selected.name || '小程序用户') }}
                </div>
                <div class="message-bubble">{{ message.content }}</div>
                <div class="message-time">{{ message.created_at }}</div>
              </div>
            </div>
          </section>

          <section class="session-meta">
            <el-input
              v-model="selected.remark"
              size="small"
              type="textarea"
              :rows="2"
              placeholder="内部备注，用户不可见"
              @blur="saveStatus"
            />
          </section>

          <footer class="reply-bar">
            <el-input
              v-model="replyContent"
              type="textarea"
              :rows="3"
              resize="none"
              placeholder="输入回复内容，发送后用户会在小程序聊天页看到"
              @keydown.native.exact.enter.prevent="sendReply"
            />
            <el-button type="primary" :loading="replying" @click="sendReply">发送</el-button>
          </footer>
        </template>

        <div v-else class="chat-placeholder">
          <i class="el-icon-chat-dot-round"></i>
          <span>请选择左侧咨询会话</span>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { listResource, updateResource } from "../api/resources";
import http from "../api/http";

const statusMap = {
  pending: "待处理",
  following: "跟进中",
  processing: "跟进中",
  closed: "已关闭",
  cancelled: "已取消"
};

export default {
  name: "ConsultationsManage",
  data() {
    return {
      loading: false,
      messageLoading: false,
      replying: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", status: "" },
      selected: null,
      selectedDetail: {},
      messages: [],
      replyContent: ""
    };
  },
  mounted() {
    this.fetchList(true);
  },
  methods: {
    statusText(status) {
      return statusMap[status] || "待处理";
    },
    avatarText(item) {
      const name = (item && (item.name || item.nickname || item.phone)) || "客";
      return String(name).slice(0, 1);
    },
    formatTime(value) {
      if (!value) return "";
      return String(value).replace("T", " ").slice(5, 16);
    },
    search() {
      this.page = 1;
      this.fetchList(true);
    },
    async fetchList(autoSelect) {
      this.loading = true;
      try {
        const res = await listResource("consultations", { ...this.filters, page: this.page, pageSize: this.pageSize });
        this.rows = res.data && res.data.items ? res.data.items : [];
        this.total = res.data && res.data.total ? res.data.total : 0;
        if (autoSelect && this.rows.length) {
          const current = this.selected && this.rows.find((item) => item.id === this.selected.id);
          this.selectSession(current || this.rows[0]);
        }
        if (!this.rows.length) {
          this.selected = null;
          this.selectedDetail = {};
          this.messages = [];
        }
      } finally {
        this.loading = false;
      }
    },
    async selectSession(item) {
      if (!item) return;
      this.selected = { ...item };
      this.selectedDetail = {};
      this.replyContent = "";
      await this.fetchMessages(item.id);
    },
    async fetchMessages(id) {
      if (!id) return;
      this.messageLoading = true;
      try {
        const res = await http.get(`/consultations/${id}/messages`, { showErrorMessage: false });
        this.selectedDetail = res.data && res.data.consultation ? res.data.consultation : {};
        this.messages = res.data && res.data.messages ? res.data.messages : [];
        this.$nextTick(this.scrollToBottom);
      } finally {
        this.messageLoading = false;
      }
    },
    scrollToBottom() {
      const list = this.$refs.messageList;
      if (list) list.scrollTop = list.scrollHeight;
    },
    async sendReply() {
      const content = String(this.replyContent || "").trim();
      if (!this.selected || !this.selected.id) return;
      if (!content) {
        this.$message.warning("请输入回复内容");
        return;
      }
      this.replying = true;
      try {
        await http.post(`/consultations/${this.selected.id}/messages`, { content });
        this.replyContent = "";
        if (this.selected.status !== "closed") this.selected.status = "following";
        await this.fetchMessages(this.selected.id);
        await this.fetchList(false);
        this.$message.success("回复已发送");
      } finally {
        this.replying = false;
      }
    },
    async saveStatus() {
      if (!this.selected || !this.selected.id) return;
      await updateResource("consultations", this.selected.id, this.selected);
      const row = this.rows.find((item) => item.id === this.selected.id);
      if (row) Object.assign(row, this.selected);
    },
    async refreshCurrent() {
      await this.fetchList(false);
      if (this.selected && this.selected.id) await this.fetchMessages(this.selected.id);
    }
  }
};
</script>

<style scoped>
.consult-workbench {
  min-width: 980px;
}

.workbench-card {
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  height: calc(100vh - 142px);
  min-height: 620px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 8px;
}

.session-pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid #e6eaf2;
  background: #f7f9fc;
}

.session-filter {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  padding: 14px;
  border-bottom: 1px solid #e6eaf2;
}

.session-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
}

.session-item {
  position: relative;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px;
  width: 100%;
  margin-bottom: 8px;
  padding: 12px 10px;
  text-align: left;
  background: #fff;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
}

.session-item.active {
  border-color: #0b55c6;
  box-shadow: 0 8px 20px rgba(11, 85, 198, 0.1);
}

.session-avatar,
.message-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #0b55c6;
  border-radius: 50%;
  font-weight: 700;
}

.session-avatar {
  width: 42px;
  height: 42px;
}

.session-main {
  min-width: 0;
}

.session-top {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.session-name {
  overflow: hidden;
  color: #20242b;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-time,
.session-source,
.session-code,
.session-preview {
  color: #7b8494;
  font-size: 12px;
}

.session-source,
.session-code,
.session-preview {
  overflow: hidden;
  margin-top: 5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-status {
  position: absolute;
  right: 10px;
  bottom: 10px;
  padding: 2px 6px;
  border-radius: 999px;
  font-size: 12px;
}

.status-pending {
  color: #b7791f;
  background: #fff7e6;
}

.status-following,
.status-processing {
  color: #0b55c6;
  background: #eaf3ff;
}

.status-closed {
  color: #606266;
  background: #f0f2f5;
}

.status-cancelled {
  color: #c23b3b;
  background: #fff0f0;
}

.session-empty,
.message-empty,
.chat-placeholder {
  color: #909399;
  text-align: center;
}

.session-empty {
  padding: 40px 0;
}

.session-pager {
  padding: 10px;
  text-align: center;
  border-top: 1px solid #e6eaf2;
}

.chat-pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: #fff;
}

.chat-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid #e6eaf2;
}

.chat-title {
  color: #20242b;
  font-size: 18px;
  font-weight: 700;
}

.chat-subtitle {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;
  color: #7b8494;
  font-size: 12px;
}

.chat-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-select {
  width: 110px;
}

.message-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 18px;
  background: #f6f8fb;
}

.message-empty {
  padding-top: 120px;
}

.message-row {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.message-row.mine {
  flex-direction: row-reverse;
}

.message-avatar {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  background: #c7a35a;
}

.message-row.mine .message-avatar {
  background: #0b55c6;
}

.message-body {
  max-width: 66%;
}

.message-row.mine .message-body {
  text-align: right;
}

.message-name,
.message-time {
  color: #909399;
  font-size: 12px;
}

.message-bubble {
  display: inline-block;
  margin: 4px 0;
  padding: 10px 12px;
  color: #303133;
  background: #fff;
  border: 1px solid #e6eaf2;
  border-radius: 8px;
  line-height: 1.55;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}

.message-row.mine .message-bubble {
  color: #fff;
  background: #0b55c6;
  border-color: #0b55c6;
}

.session-meta {
  padding: 10px 18px;
  border-top: 1px solid #e6eaf2;
}

.reply-bar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 96px;
  gap: 12px;
  align-items: end;
  padding: 14px 18px 18px;
  border-top: 1px solid #e6eaf2;
}

.chat-placeholder {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 14px;
}

.chat-placeholder i {
  font-size: 42px;
}
</style>
